import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-destroy-key-flow.css?inline';
import { formatUtc } from '../../lib/portal-data.js';
import {
    confirmationText, confirms, fillKeyUrl, parseJsonRecord, sameActor, type PkKeyImpact, type PkKeyRecord,
} from '../../lib/keys.js';
import { formFields, requestThroughGate } from '../../lib/step-up.js';
import { impactSummary } from '../pk-key-detail/pk-key-detail.js';
import '../pk-alert/pk-alert.js';
import '../pk-button/pk-button.js';

/** Which destroy action a form carries. */
export type PkDestroyAction = 'request' | 'approve' | 'cancel' | 'override';

/** What the viewer sees for a key, from the contract's `state` / `pendingDestroy` / `tombstone` and the viewer. */
export type PkDestroyView = 'tombstone' | 'override' | 'approver' | 'requester' | 'pending-readonly' | 'request' | 'none';

const DEFAULT_ACTIONS: Record<PkDestroyAction, string> = {
    request: '/portal/admin/keys/{id}/destroy-requests',
    approve: '/portal/admin/keys/{id}/destroy-requests/{requestId}/approve',
    cancel: '/portal/admin/keys/{id}/destroy-requests/{requestId}/cancel',
    override: '/portal/staff/keys/{id}/destroy-requests/{requestId}/override',
};

/**
 * The view for a viewer (PortalKit keys spec §3): a destroyed key shows its tombstone; a pending request shows
 * staff the override, a **different** Owner the approver view, the requesting Owner a waiting view (both may
 * cancel), anyone else a read-only notice; an Owner may request the destroy of a key in any other state.
 */
export function destroyView(key: PkKeyRecord, currentUser: string, roleLevel: number, isStaff: boolean): PkDestroyView {
    if (key.state === 'destroyed') return key.tombstone ? 'tombstone' : 'none';
    if (key.state === 'destroy-pending' && key.pendingDestroy) {
        if (isStaff) return 'override';
        if (roleLevel >= 4) return sameActor(currentUser, key.pendingDestroy.requestedBy) ? 'requester' : 'approver';
        return 'pending-readonly';
    }
    if (!isStaff && roleLevel >= 4 && ['active', 'retired', 'disabled', 'revoked'].includes(key.state)) return 'request';
    return 'none';
}

/**
 * `<pk-destroy-key-flow>` — the two-person crypto-shred (PortalKit keys spec §2–§3), replacing a bare
 * `confirm()`:
 *
 *   - **Request** (an Owner): the impact summary → a typed confirmation (the key's id or thumbprint) and a reason →
 *     the request, through the step-up gate (fresh TOTP) → "request sent — a second Owner must approve it within
 *     7 days".
 *   - **Approve** (a second, different Owner): the pending request with requester and expiry → Approve (step-up).
 *   - **Cancel** (any single Owner, the requester included).
 *   - **Override** (staff): destroy alone with a required reason (step-up).
 *   - A destroyed key shows its **tombstone** (who requested, who approved or overrode, the reason).
 *
 * Every action is a form POST that goes through an enclosing `<pk-step-up-gate>` (else it posts natively).
 *
 * Attributes:
 *   - `key`           — JSON of the key (contract `Key`); the `key` property wins once set.
 *   - `impact`        — JSON of its impact (contract `KeyImpact`).
 *   - `current-user`  — the viewer's email (decides approver vs requester).
 *   - `role-level`    — the viewer's role level (Owner = 4).
 *   - `is-staff`      — boolean; the staff override view.
 *   - `request-action`, `approve-action`, `cancel-action`, `override-action` — POST URL templates (`{id}`,
 *     `{requestId}`); defaults under `/portal/admin/keys/{id}/destroy-requests` and `/portal/staff/keys`.
 *   - `csrf-token` / `csrf-name` — anti-forgery field of every form.
 *
 * Events:
 *   - `pk-destroy-action` — `{ action, keyId, requestId, ok }` after a gated action finished.
 */
export class PkDestroyKeyFlow extends HTMLElement {
    static readonly tagName = 'pk-destroy-key-flow';

    static get observedAttributes(): string[] {
        return [
            'key', 'impact', 'current-user', 'role-level', 'is-staff', 'csrf-token', 'csrf-name',
            'request-action', 'approve-action', 'cancel-action', 'override-action',
        ];
    }

    private readonly _root: ShadowRoot;
    private _key: PkKeyRecord | null | undefined;
    private _phase: 'idle' | 'impact' | 'confirm' | 'sent' = 'idle';
    private _typed = '';
    private _reason = '';

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'key') this._key = undefined;
        if (this.isConnected) this._render();
    }

    /** The key (property wins over the attribute once set). */
    get key(): PkKeyRecord | null {
        return this._key !== undefined ? this._key : parseJsonRecord<PkKeyRecord>(this.getAttribute('key'));
    }

    set key(value: PkKeyRecord | null) {
        this._key = value ?? null;
        if (this.isConnected) this._render();
    }

    /** The current view (see `destroyView`). */
    get view(): PkDestroyView {
        const key = this.key;
        return key ? destroyView(key, this.getAttribute('current-user') ?? '', this._roleLevel, this.hasAttribute('is-staff')) : 'none';
    }

    private get _roleLevel(): number {
        const n = Number(this.getAttribute('role-level') ?? '0');
        return Number.isFinite(n) ? Math.trunc(n) : 0;
    }

    private _url(action: PkDestroyAction, key: PkKeyRecord): string {
        return fillKeyUrl(this.getAttribute(`${action}-action`) ?? DEFAULT_ACTIONS[action], key.id, key.pendingDestroy?.requestId ?? '');
    }

    private _go(phase: 'idle' | 'impact' | 'confirm'): void {
        this._phase = phase;
        if (phase !== 'confirm') {
            this._typed = '';
            this._reason = '';
        }
        this._render();
    }

    private readonly _onTyped = (e: Event): void => {
        this._typed = (e.target as HTMLInputElement).value;
        this._render();
    };

    private readonly _onReason = (e: Event): void => {
        this._reason = (e.target as HTMLTextAreaElement).value;
        this._render();
    };

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        const action = form.dataset.destroyAction as PkDestroyAction | undefined;
        const key = this.key;
        if (!action || !key) return;
        if (action === 'request' && !(confirms(key, this._typed) && this._reason.trim())) {
            e.preventDefault();
            return;
        }
        const routed = requestThroughGate(this, {
            url: form.getAttribute('action') ?? '',
            fields: formFields(form),
            label: LABELS[action] + ' ' + key.id,
        });
        if (!routed) return; // no gate: native post
        e.preventDefault();
        void routed.then((outcome) => {
            if (action === 'request' && outcome.ok) {
                this._phase = 'sent';
                this._render();
            }
            this.dispatchEvent(new CustomEvent('pk-destroy-action', {
                bubbles: true, composed: true,
                detail: { action, keyId: key.id, requestId: key.pendingDestroy?.requestId ?? null, ok: outcome.ok },
            }));
        });
    };

    private _csrf(): TemplateResult | typeof nothing {
        const token = this.getAttribute('csrf-token') ?? '';
        const name = this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
        return token ? html`<input type="hidden" name=${name} .value=${token} />` : nothing;
    }

    private _form(action: PkDestroyAction, key: PkKeyRecord, body: TemplateResult, button: TemplateResult): TemplateResult {
        return html`
            <form class="destroy-form ${action}" method="post" action=${this._url(action, key)} data-destroy-action=${action}>
                ${this._csrf()}
                ${body}
                ${button}
            </form>
        `;
    }

    private _pending(key: PkKeyRecord): TemplateResult {
        const p = key.pendingDestroy!;
        return html`
            <p class="pending-summary" role="status">
                Destroy requested by <strong class="requested-by">${p.requestedBy}</strong> on ${formatUtc(p.requestedAtUtc)}.
                A second, different Owner must approve it by <span class="expires">${formatUtc(p.expiresAtUtc)}</span>, or it expires.
            </p>
        `;
    }

    private _cancelForm(key: PkKeyRecord): TemplateResult {
        return this._form('cancel', key, html``, html`<pk-button variant="secondary" type="submit">Cancel the request</pk-button>`);
    }

    private _requestFlow(key: PkKeyRecord, impact: PkKeyImpact | null): TemplateResult {
        const expected = confirmationText(key);
        switch (this._phase) {
            case 'sent':
                return html`<pk-alert variant="success" class="sent" role="status">Request sent. A second Owner must approve it within 7 days, or it expires.</pk-alert>`;
            case 'impact':
                return html`
                    <section class="step impact">
                        <h4>1. What is destroyed</h4>
                        <p class="impact-summary">${impact ? impactSummary(impact) : 'Impact unavailable.'}</p>
                        <p class="consequence">Destroying the key makes everything it protects unreadable, for good. The key stays listed as a tombstone.</p>
                        <div class="buttons">
                            <pk-button class="back" variant="secondary" type="button" @click=${() => this._go('idle')}>Back</pk-button>
                            <pk-button class="continue" variant="danger" type="button" @click=${() => this._go('confirm')}>Continue</pk-button>
                        </div>
                    </section>
                `;
            case 'confirm': {
                const ready = confirms(key, this._typed) && this._reason.trim() !== '';
                return html`
                    <section class="step confirm">
                        <h4>2. Confirm</h4>
                        ${this._form('request', key, html`
                            <label class="field">
                                <span>Type <code class="expected">${expected}</code> to confirm</span>
                                <input class="typed" name="confirmation" autocomplete="off" spellcheck="false" .value=${this._typed} @input=${this._onTyped} />
                            </label>
                            <label class="field">
                                <span>Reason (kept on the tombstone)</span>
                                <textarea class="reason" name="reason" rows="2" maxlength="500" .value=${this._reason} @input=${this._onReason}></textarea>
                            </label>
                        `, html`
                            <div class="buttons">
                                <pk-button class="back" variant="secondary" type="button" @click=${() => this._go('impact')}>Back</pk-button>
                                <pk-button class="request-submit" variant="danger" type="submit" ?disabled=${!ready}>Request destroy</pk-button>
                            </div>
                        `)}
                        <p class="note">Your authenticator code is asked next. A second Owner must then approve.</p>
                    </section>
                `;
            }
            default:
                return html`<pk-button class="start" variant="danger" type="button" @click=${() => this._go('impact')}>Destroy this key…</pk-button>`;
        }
    }

    private template(): TemplateResult {
        const key = this.key;
        const view = this.view;
        if (!key || view === 'none') return html`<style>${styles}</style>`;
        const impact = parseJsonRecord<PkKeyImpact>(this.getAttribute('impact'));
        let body: TemplateResult;
        switch (view) {
            case 'tombstone': {
                const t = key.tombstone!;
                body = html`
                    <div class="tombstone" role="status">
                        <p>Destroyed ${formatUtc(key.destroyedAtUtc)}.</p>
                        <p class="who">${t.override
                            ? html`Destroyed by staff override: <strong class="destroyed-by">${t.destroyedBy}</strong>.`
                            : html`Requested by <strong class="destroyed-by">${t.destroyedBy}</strong>, approved by <strong class="approved-by">${t.approvedBy ?? '—'}</strong>.`}</p>
                        ${t.reason ? html`<p class="tomb-reason">Reason: ${t.reason}</p>` : nothing}
                    </div>
                `;
                break;
            }
            case 'override':
                body = html`
                    ${this._pending(key)}
                    ${this._form('override', key, html`
                        <label class="field">
                            <span>Reason for destroying without a second Owner (required)</span>
                            <input class="override-reason" name="reason" required maxlength="500" autocomplete="off" />
                        </label>
                    `, html`<pk-button variant="danger" type="submit">Destroy now (staff override)</pk-button>`)}
                `;
                break;
            case 'approver':
                body = html`
                    ${this._pending(key)}
                    <p class="approver-note">You can approve as the second Owner. Approving destroys the key now.</p>
                    <div class="buttons">
                        ${this._form('approve', key, html``, html`<pk-button variant="danger" type="submit">Approve and destroy</pk-button>`)}
                        ${this._cancelForm(key)}
                    </div>
                `;
                break;
            case 'requester':
                body = html`
                    ${this._pending(key)}
                    <p class="waiting">You requested this. Another Owner must approve it.</p>
                    <div class="buttons">${this._cancelForm(key)}</div>
                `;
                break;
            case 'pending-readonly':
                body = this._pending(key);
                break;
            default:
                body = this._requestFlow(key, impact);
        }
        return html`<style>${styles}</style><div class="flow" data-view=${view}>${body}</div>`;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

const LABELS: Record<PkDestroyAction, string> = {
    request: 'Request destroy of',
    approve: 'Approve destroy of',
    cancel: 'Cancel destroy of',
    override: 'Override destroy of',
};

if (!customElements.get(PkDestroyKeyFlow.tagName)) {
    customElements.define(PkDestroyKeyFlow.tagName, PkDestroyKeyFlow);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-destroy-key-flow': PkDestroyKeyFlow;
    }
}
